import type { GetServerSidePropsContext, NextPage } from 'next';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import tracking, { TrackingContentGroup } from 'src/tracking';
import { isMaintenance, set503Header, setDefaultCacheHeader, setNoCacheHeader, ssrPersistedQuery, transformSeoToMetas } from '../../helpers/SSRHelpers';
import { initWithLangAndLabels } from '../../i18n';
import cheesePage from '../../graphql/Services/Cheese/queries/cheesePage';
import { ProductPopinProps } from '../../components/molecules/ProductPopin';
import productPopinTransform from '../../relay/ProductPopin/transform';
import CheeseTemplate from '../../templates/CheeseTemplate';
import { Cheese, Format, PageTemplate, Recipe, Tag } from '../../graphql/generated/api-graphql';
import useCheeseHero from '../../relay/Cheese/useCheeseHero';
import { CheeseMainContentProps } from '../../components/molecules/CheeseMainContent';
import { Icons } from '../../components/atoms/Icon';
import Router from '../../routes/Router';
import routes from '../../routes/definitions';
import { CardsBlockProps } from '../../components/molecules/CardsBlock';
import { transformRecipeTinyCard } from '../../relay/RecipeCard/transformRecipeTinyCard';
import { RelatedProductsBlockProps } from '../../components/molecules/RelatedProductsBlock';
import { transformTagForCloud } from '../../relay/Common/tag';
import BrandPush from '../../components/molecules/BrandPush';
import BrandPushTransform from '../../relay/BrandPush/transform';
import { toRange } from '../../relay/Common/range';
import cheeseMainContentTransformer from '../../relay/Cheese/cheeseMainContentTransformer';
import highlightTransformer from '../../relay/Common/highlightTransformer';
import type { BreadcrumbProps } from '../../components/molecules/Breadcrumb';
import { Configuration } from '../../graphql/Services';
import { TagsCloudProps } from '../../components/molecules/TagsCloud';
import { brandArticlesTransformer } from '../../relay/Brand/brandArticlesTranformer';
import { GetServerSidePropsReturnType, PageProps } from '../../relay/Common/PageProps';
import BugsnagHelpers from '../../helpers/BugsnagHelpers';
import BreadcrumbWrapper from '../../relay/Breadcrumb/BreadcrumbWrapper';
import HeaderWrapper from '../../relay/Header/HeaderWrapper';
import FooterWrapper from '../../relay/Footer/FooterWrapper';
type CheesePageProps = PageProps & {
  data: Cheese;
  recipes: Recipe[];
};
const CheesePage: NextPage<CheesePageProps> = ({
  data,
  recipes,
  configuration
}) => {
  const {
    t
  } = useTranslation();
  const [openOpin, setOpenPopin] = useState(false);
  const [format, setFormat] = useState<any>(null);
  const popinData = productPopinTransform({
    t,
    data: {
      format: format
    },
    displayMoreButton: false
  });
  const handleLoadOpenPopin = (format: Format) => {
    setFormat(format);
    if (popinData) {
      setOpenPopin(true);
    }
  };
  const handleClose = useCallback(() => {
    setFormat(null);
    setOpenPopin(false);
  }, []);

  // ------
  // main

  const breadcrumbEntries: BreadcrumbProps['entries'] = useMemo(() => [{
    label: configuration?.pagesByTemplate?.[PageTemplate.HpCheeses]?.title ?? '',
    href: configuration?.pagesByTemplate?.[PageTemplate.HpCheeses]?.slug ?? ''
  }, {
    label: configuration?.pagesByTemplate?.[PageTemplate.ListCheeses]?.title ?? t('breadcrumb_brands'),
    href: configuration?.pagesByTemplate?.[PageTemplate.ListCheeses]?.slug
  }, {
    label: data?.title
  }], [configuration, data, t]);
  const heroProps = useCheeseHero(data);
  const mainContentProps: CheeseMainContentProps = cheeseMainContentTransformer(t, data);
  const highlightProps = highlightTransformer(t, data, handleLoadOpenPopin);

  // ------
  // sidebar

  // ------
  // related

  const allrecipes = [...data.recipes, ...recipes];
  const relatedRecipesProps: CardsBlockProps | undefined = allrecipes && allrecipes?.length > 0 ? {
    title: t('brand_recipes_title'),
    tags: data?.tags?.map((tag: Tag) => transformTagForCloud(tag, data.title)),
    cards: allrecipes?.map(item => {
      return transformRecipeTinyCard(item, t('brand_recipes_title'));
    }).slice(0, 9),
    actionButtonProps: {
      label: t('more_recipes'),
      iconPosition: 'right',
      iconProps: {
        icon: Icons.arrowRight
      },
      href: configuration?.pagesByTemplate?.[PageTemplate.HpRecipes]?.slug,
      onClick: () => tracking.cta(t('more_recipes'), t('brand_recipes_title'))
    }
  } : undefined;
  const relatedProductsProps: RelatedProductsBlockProps | undefined = data.formats && data.formats.length > 0 ? {
    title: t('the_whole_family'),
    rangeTitle: data?.rangeTitle || undefined,
    rangeBody: data?.rangeBody || undefined,
    ranges: [toRange(t, data, handleLoadOpenPopin, true)],
    ecopush: {
      iconProps: {
        icon: Icons.triman
      },
      htmlProps: {
        text: t('this_logo_ecopush_')
      }
    }
  } : undefined;
  const tagsCloudProps: TagsCloudProps = {
    title: t('more_cheese'),
    tags: [...(data.tags && data.tags.length > 0 ? data.tags.map(tag => transformTagForCloud(tag, data.title)) : [])]
  };
  const articlesProps: CardsBlockProps | undefined = brandArticlesTransformer(t, data?.brand);
  // todo : get current cheese related articles when no-brand cheese

  // ------
  // popin

  const productPopinProps: ProductPopinProps | undefined = popinData ? {
    ...popinData,
    popin: {
      ...popinData.popin,
      isOpen: openOpin,
      closeHandler: handleClose
    }
  } : undefined;
  useEffect(() => {
    tracking.page({
      brand: undefined,
      group: TrackingContentGroup.CHEESE,
      page: data?.title,
      section: undefined,
      hideRubrique: true
    });
  }, [data?.title]);
  return <>
      <CheeseTemplate idContent="Content" header={<HeaderWrapper data={configuration?.header} socials={configuration?.footer?.socialLinks} />} footer={<FooterWrapper data={configuration?.footer} />} breadcrumb={<BreadcrumbWrapper entries={breadcrumbEntries} />} heroProps={heroProps} mainContentProps={mainContentProps} highlightProps={highlightProps} productPopinProps={productPopinProps} relatedRecipesProps={relatedRecipesProps} relatedProductsProps={relatedProductsProps} tagsCloudProps={tagsCloudProps} articlesProps={articlesProps} sideContent={<>
            {data.brand && <BrandPush {...BrandPushTransform({
        t: t,
        brand: data.brand,
        formats: undefined,
        handlePopin: handleLoadOpenPopin
      })} />}
          </>} />
    </>;
};
export default CheesePage;
export async function getServerSideProps(context: GetServerSidePropsContext): GetServerSidePropsReturnType<CheesePageProps> {
  try {
    const [rs, configurationRs] = await Promise.all([ssrPersistedQuery(cheesePage, {
      slug: context?.query?.slug
    }), ssrPersistedQuery(Configuration.queries.configuration)]);
    const {
      cheese: data,
      recipes
    } = rs?.data ?? {};
    const configuration = configurationRs?.data ?? null;
    const i18n = initWithLangAndLabels('fr', {});
    console.log('configuration', configuration);
    if (!configuration || rs?.errors) {
      set503Header(context);
      return {
        props: {
          i18n,
          error: true
        }
      };
    }
    if (isMaintenance(configuration)) {
      set503Header(context);
    } else {
      setDefaultCacheHeader(context);
    }
    if (!data) {
      return {
        notFound: true,
        props: {
          i18n,
          configuration
        }
      };
    }
    return {
      props: {
        i18n,
        configuration,
        metas: transformSeoToMetas(Router.getRouteUrl(routes.cheese, {
          slug: context?.query?.slug
        }), data?.seo),
        data,
        recipes
      }
    };
  } catch (e: any) {
    BugsnagHelpers?.notify(e, event => {
      const {
        resolvedUrl,
        query,
        params
      } = context;
      event.addMetadata('context', {
        resolvedUrl,
        query,
        params
      });
    });
    setNoCacheHeader(context);
    return {
      notFound: true
    };
  }
}